/**
 * Générateur de salles d'Incarnam (règles R1 à R11 de research/cartes/logique-incarnam.md).
 * Entrée : contenu (sous-zones, tuiles), id de sous-zone, seed. Sortie : une RoomDef avec sa grille.
 * Tout l'aléatoire vient de createRng(seed) : même seed et même sous-zone donnent la même salle.
 *
 * Unité « cellule » : coordonnées pixel divisées par (CELL_W, CELL_H). Deux cellules voisines par un côté sont
 * à (0,5 ; 0,5) l'une de l'autre, deux voisines par un sommet à 1. La salle couvre [0, 30] x [0, 34].
 */
import type { BiomeDef, Content, ExitSide, PlacedTile, RoomDef, TileDef } from '../../data/schema';
import { createRng, type Rng } from '../rng';
import type { Vec2 } from '../types';
import { bfs, Bitset, CELL_H, CELL_W, CELLS, cellAt, centerOf, COLS, idx, isValidIdx, ROOM_H, ROOM_W, ROWS, sideNeighbours } from './grid';
import { splitMask, transitionKey, transitions } from './autotile';
import { createNoise } from './noise';

const SPAN_X = ROOM_W / CELL_W; // 30
const SPAN_Y = ROOM_H / CELL_H; // 34
const OPEN = 0, BORDER = 1, PATH = 2, WATER = 3, PLOT = 4, PONTOON = 5, VOID = 255;

interface Ctx {
  rng: Rng; b: BiomeDef; content: Content;
  kind: Uint8Array; protect: Uint8Array; blocked: Uint8Array;
  objects: PlacedTile[]; decals: PlacedTile[];
  cx: Float32Array; cy: Float32Array; // centres en unités cellule
}

/**
 * Cellules bloquées par un objet ancré en `cell` : l'ancre et les décalages `footprint` de la tuile (en cellules,
 * mesurés par tools/measure-footprints.py depuis la base du sprite) ; les décalages hors grille sont ignorés.
 */
export function footprintCells(tiles: Record<string, TileDef>, key: string, cell: number): number[] {
  const fp = tiles[key]?.footprint;
  if (!fp || fp.length <= 1) return [cell];
  const c = centerOf(cell);
  const out = [cell];
  for (const [dx, dy] of fp) {
    if (dx === 0 && dy === 0) continue;
    const i = cellAt(c.x + dx * CELL_W, c.y + dy * CELL_H);
    if (i >= 0 && !out.includes(i)) out.push(i);
  }
  return out;
}

const range = (rng: Rng, [a, b]: [number, number] | readonly number[]): number => a! + rng.next() * (b! - a!);
const irange = (rng: Rng, [a, b]: [number, number] | readonly number[]): number => rng.int(Math.round(a!), Math.round(b!));
const edgeDist = (x: number, y: number): number => Math.min(x, SPAN_X - x, y, SPAN_Y - y);

function shuffled(rng: Rng, n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = rng.int(0, i); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}

/** Cellules valides dont le centre est à moins de `r` (unités cellule) du point (x, y). */
function cellsWithin(ctx: Ctx, x: number, y: number, r: number): number[] {
  const out: number[] = [];
  const r0 = Math.max(0, Math.floor((y - r) * 2 - 1)), r1 = Math.min(ROWS - 1, Math.ceil((y + r) * 2));
  const c0 = Math.max(0, Math.floor(x - r - 1)), c1 = Math.min(COLS - 1, Math.ceil(x + r));
  for (let row = r0; row <= r1; row++) for (let col = c0; col <= c1; col++) {
    const i = idx(row, col);
    if (ctx.kind[i] === VOID) continue;
    if (Math.hypot(ctx.cx[i]! - x, ctx.cy[i]! - y) < r) out.push(i);
  }
  return out;
}

function place(ctx: Ctx, list: PlacedTile[], key: string, cell: number, jitter = 0.12): void {
  const c = centerOf(cell);
  const { rng } = ctx;
  list.push({ key, cell, flip: rng.chance(0.5), x: c.x + (rng.next() * 2 - 1) * jitter * CELL_W, y: c.y + (rng.next() * 2 - 1) * jitter * 0.5 * CELL_H });
}

/**
 * Pose un objet si toute son empreinte est libre : cellules de même nature que l'ancre (bordure, eau, parcelle) ou
 * cellules ouvertes non protégées, jamais déjà bloquées (les empreintes ne se chevauchent pas). Un objet bloquant
 * bloque toute son empreinte, pas seulement son ancre (le joueur ne marche pas dans un tronc couché). Renvoie false
 * si l'objet n'a pas été posé.
 */
function placeObject(ctx: Ctx, key: string, cell: number, jitter = 0.12): boolean {
  const blocks = ctx.content.tiles[key]?.block === 1;
  const cells = blocks ? footprintCells(ctx.content.tiles, key, cell) : [cell];
  if (blocks) {
    const anchorKind = ctx.kind[cell];
    for (const c of cells) {
      if (ctx.blocked[c]) return false;
      if (ctx.kind[c] === anchorKind && c === cell) continue;
      if (ctx.kind[c] !== anchorKind && !(ctx.kind[c] === OPEN && !ctx.protect[c])) return false;
      if (ctx.kind[c] === OPEN && ctx.protect[c]) return false;
    }
  }
  place(ctx, ctx.objects, key, cell, jitter);
  if (blocks) for (const c of cells) ctx.blocked[c] = 1;
  return true;
}

function nearObject(ctx: Ctx, x: number, y: number, r: number, keys?: Set<string>): boolean {
  for (const o of ctx.objects) {
    if (keys && !keys.has(o.key)) continue;
    if (Math.hypot(o.x / CELL_W - x, o.y / CELL_H - y) < r) return true;
  }
  return false;
}

// --- 1. Gabarit : sorties et départ ---------------------------------------------------------------
interface Exit { side: ExitSide; ex: number; ey: number; }
const SIDES: ExitSide[] = ['nord', 'sud', 'ouest', 'est'];

function chooseExits(rng: Rng): Exit[] {
  const n = rng.chance(0.5) ? 1 : 2;
  const order = shuffled(rng, 4).map((k) => SIDES[k]!);
  return order.slice(0, n).map((side) => {
    const t = 0.3 + rng.next() * 0.4;
    if (side === 'nord') return { side, ex: t * SPAN_X, ey: 0 };
    if (side === 'sud') return { side, ex: t * SPAN_X, ey: SPAN_Y };
    if (side === 'ouest') return { side, ex: 0, ey: t * SPAN_Y };
    return { side, ex: SPAN_X, ey: t * SPAN_Y };
  });
}

// --- 2. Chemins (R8) ----------------------------------------------------------------------------
/** Chemin sinueux de `from` à `to` (largeur = rayon en cellules), toujours à l'intérieur de la bordure fermée. */
function paintPath(ctx: Ctx, from: Vec2, to: Vec2, width: number): void {
  const { rng } = ctx;
  const dx = to.x - from.x, dy = to.y - from.y, len = Math.hypot(dx, dy);
  if (len < 0.01) return;
  const px = -dy / len, py = dx / len;
  const amp = 1 + rng.next() * 2.2, freq = 0.6 + rng.next() * 0.8, phase = rng.next() * Math.PI * 2;
  const n = Math.ceil(len * 4);
  for (let k = 0; k <= n; k++) {
    const t = k / n;
    const off = amp * Math.sin(Math.PI * t) * Math.sin(2 * Math.PI * freq * t + phase);
    const x = from.x + dx * t + px * off, y = from.y + dy * t + py * off;
    if (edgeDist(x, y) < ctx.b.border.depth[0] + 0.6 + width) continue;
    for (const i of cellsWithin(ctx, x, y, width)) { ctx.kind[i] = PATH; ctx.protect[i] = 1; }
    for (const i of cellsWithin(ctx, x, y, width + 0.75)) ctx.protect[i] = 1;
  }
}

/** Couloir droit de 3 cellules du bord (point de sortie) jusqu'à l'intérieur de la bordure ; renvoie son extrémité intérieure. */
function paintCorridor(ctx: Ctx, e: Exit): Vec2 {
  const n = e.side === 'nord' ? { x: 0, y: 1 } : e.side === 'sud' ? { x: 0, y: -1 } : e.side === 'ouest' ? { x: 1, y: 0 } : { x: -1, y: 0 };
  const depth = ctx.b.border.depth[1] + 1.6;
  const steps = Math.ceil(depth * 4);
  for (let k = 0; k <= steps; k++) {
    const x = e.ex + (n.x * depth * k) / steps, y = e.ey + (n.y * depth * k) / steps;
    for (const i of cellsWithin(ctx, x, y, 1.45)) { ctx.kind[i] = PATH; ctx.protect[i] = 1; }
    for (const i of cellsWithin(ctx, x, y, 2.1)) ctx.protect[i] = 1;
  }
  return { x: e.ex + n.x * depth, y: e.ey + n.y * depth };
}

// --- 3. Éléments propres aux sous-zones ------------------------------------------------------------
function farCell(ctx: Ctx, start: Vec2, minStart: number, minEdge: number, tries = 60): number {
  for (let k = 0; k < tries; k++) {
    const i = ctx.rng.int(0, CELLS - 1);
    if (ctx.kind[i] !== OPEN || ctx.protect[i]) continue;
    if (Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y) < minStart) continue;
    if (edgeDist(ctx.cx[i]!, ctx.cy[i]!) < minEdge) continue;
    return i;
  }
  return -1;
}

function feature(ctx: Ctx, start: Vec2): void {
  const { b, rng } = ctx;
  const f = b.feature;
  if (f.type === 'lake') {
    const target = range(rng, f.fraction) * 1977;
    const ratio = 1.2 + rng.next() * 0.6;
    const ry0 = Math.sqrt((target * 0.5) / (Math.PI * ratio)), rx0 = ry0 * ratio;
    const noise = createNoise(rng, 3);
    // le chemin contourne le lac : centre choisi parmi 12 candidats loin du départ, sans cellule de chemin dans
    // l'ellipse élargie d'une cellule ; à défaut, lac réduit de 20 %, puis ponton sur le chemin
    const paths: number[] = [];
    for (let i = 0; i < CELLS; i++) if (ctx.kind[i] === PATH) paths.push(i);
    const crosses = (x0: number, y0: number, rx: number, ry: number): boolean => paths.some((i) => ((ctx.cx[i]! - x0) / (rx + 1.1)) ** 2 + ((ctx.cy[i]! - y0) / (ry + 1.1)) ** 2 < 1);
    const cand: number[] = [];
    for (let k = 0; k < 12; k++) { const i = farCell(ctx, start, 6, 4.5 + ry0 * 0.6); if (i >= 0) cand.push(i); }
    let best = -1, rx = rx0, ry = ry0;
    for (let shrink = 1; shrink >= 0.75 && best < 0; shrink *= 0.8) {
      rx = rx0 * shrink; ry = ry0 * shrink;
      let bestD = -1;
      for (const i of cand) {
        if (crosses(ctx.cx[i]!, ctx.cy[i]!, rx, ry)) continue;
        const d = Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y);
        if (d > bestD) { bestD = d; best = i; }
      }
    }
    if (best < 0) {
      rx = rx0; ry = ry0;
      let bestD = -1;
      for (const i of cand) { const d = Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y); if (d > bestD) { bestD = d; best = i; } }
    }
    if (best < 0) return;
    const x0 = ctx.cx[best]!, y0 = ctx.cy[best]!;
    for (let i = 0; i < CELLS; i++) {
      if (ctx.kind[i] !== OPEN) continue;
      const e = ((ctx.cx[i]! - x0) / rx) ** 2 + ((ctx.cy[i]! - y0) / ry) ** 2 + (noise(ctx.cx[i]!, ctx.cy[i]!) - 0.5) * 0.7;
      if (e < 1 && edgeDist(ctx.cx[i]!, ctx.cy[i]!) > 3) { ctx.kind[i] = WATER; ctx.protect[i] = 1; }
    }
    // chemin bordé d'eau par un côté : ponton de planches (marchable, pas de dalles)
    for (const i of paths) if (sideNeighbours(i).some((k) => ctx.kind[k] === WATER)) ctx.kind[i] = PONTOON;
    // rive : roseaux, nénuphars sur l'eau près du bord, rochers dans l'eau
    const water: number[] = [];
    for (let i = 0; i < CELLS; i++) if (ctx.kind[i] === WATER) water.push(i);
    for (const i of water) {
      const land = sideNeighbours(i).filter((k) => ctx.kind[k] === OPEN);
      if (land.length && rng.chance(0.18)) place(ctx, ctx.decals, rng.pick(f.floating), i, 0.15);
      for (const k of land) if (!ctx.protect[k] && !ctx.blocked[k] && rng.chance(0.22)) placeObject(ctx, rng.pick(f.reeds), k, 0.2);
    }
    for (let k = 0; k < 3 && water.length; k++) placeObject(ctx, rng.pick(f.rocks), rng.pick(water), 0.1);
  } else if (f.type === 'plots') {
    const n = irange(rng, f.count);
    let made = 0;
    for (let attempt = 0; attempt < 80 && made < n; attempt++) {
      const w = rng.int(3, 5), h = rng.int(5, 9);
      const r0 = rng.int(4, ROWS - 5 - h), c0 = rng.int(3, COLS - 4 - w);
      const cells: number[] = [];
      let ok = true;
      for (let r = r0; r < r0 + h && ok; r++) for (let c = c0; c < c0 + w - (r & 1); c++) {
        const i = idx(r, c);
        if (ctx.kind[i] !== OPEN || ctx.protect[i] || Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y) < 4.5) { ok = false; break; }
        cells.push(i);
      }
      // une rangée de marge marchable autour de la parcelle
      if (!ok || cells.some((i) => sideNeighbours(i).some((k) => ctx.kind[k] === PLOT))) continue;
      for (const i of cells) { ctx.kind[i] = PLOT; ctx.protect[i] = 1; }
      for (const i of cells) {
        if (rng.chance(0.55)) place(ctx, ctx.decals, rng.pick(f.straw), i, 0.05);
        if (rng.chance(0.8)) placeObject(ctx, rng.pick(f.crops), i, 0.15);
      }
      for (const i of [cells[0]!, cells[cells.length - 1]!]) placeObject(ctx, rng.pick(f.stakes), i, 0.05);
      for (const i of cells) ctx.blocked[i] = 1;
      made++;
    }
  } else if (f.type === 'graves') {
    let left = irange(rng, f.count);
    for (let attempt = 0; attempt < 30 && left > 0; attempt++) {
      const i0 = farCell(ctx, start, 4, 6);
      if (i0 < 0) continue;
      const r = Math.floor(i0 / COLS), c0 = i0 % COLS;
      const len = rng.int(3, 6);
      for (let k = 0; k < len && left > 0; k++) {
        const i = idx(r, c0 + k);
        if (!isValidIdx(i) || c0 + k >= COLS - 3 || ctx.kind[i] !== OPEN || ctx.protect[i] || ctx.blocked[i]) continue;
        if (rng.chance(0.85) && placeObject(ctx, rng.pick(f.graves), i, 0.04)) left--;
      }
    }
  } else if (f.type === 'clearing') {
    const R = range(rng, f.radius);
    for (const i of cellsWithin(ctx, start.x, start.y, R)) ctx.protect[i] = 1;
  }
}

// --- 4. Bordure fermée (R2, R3) ------------------------------------------------------------------
function border(ctx: Ctx): void {
  const { b, rng } = ctx;
  const noise = createNoise(rng, 4);
  const [d0, d1] = b.border.depth;
  for (let i = 0; i < CELLS; i++) {
    if (ctx.kind[i] !== OPEN) continue;
    const x = ctx.cx[i]!, y = ctx.cy[i]!;
    const depth = d0! + (d1! - d0!) * noise(x, y);
    const ex = Math.min(x, SPAN_X - x), ey = Math.min(y, SPAN_Y - y);
    if (Math.min(ex, ey) < depth || (ex < depth + 1.5 && ey < depth + 1.5)) ctx.kind[i] = BORDER;
  }
  const large = new Set(b.border.large);
  // grands éléments espacés, en commençant par la lisière intérieure (visible), puis le reste de la bande
  const cells = shuffled(rng, CELLS).filter((i) => ctx.kind[i] === BORDER);
  const inner = (i: number): boolean => sideNeighbours(i).some((k) => ctx.kind[k] !== BORDER && ctx.kind[k] !== VOID);
  for (const pass of [true, false]) for (const i of cells) {
    if (inner(i) !== pass) continue;
    const sp = b.border.spacing * (pass ? 1 : 1.25);
    if (!nearObject(ctx, ctx.cx[i]!, ctx.cy[i]!, sp, large)) placeObject(ctx, rng.pick(b.border.large), i, 0.18);
  }
  // moyens dans les trous de la lisière intérieure
  for (const i of cells) if (inner(i) && !nearObject(ctx, ctx.cx[i]!, ctx.cy[i]!, 0.75)) placeObject(ctx, rng.pick(b.border.medium), i, 0.15);
  for (const i of cells) ctx.blocked[i] = 1;
}

// --- 5. Obstacles intérieurs (R4, R5) -------------------------------------------------------------
function interior(ctx: Ctx, start: Vec2): void {
  const { b, rng } = ctx;
  const free = (i: number): boolean => ctx.kind[i] === OPEN && !ctx.protect[i] && !ctx.blocked[i];
  let open = 0;
  for (let i = 0; i < CELLS; i++) if (ctx.kind[i] === OPEN) open++;
  let budget = Math.round(b.interior.obstacle * open);
  const groves = irange(rng, b.interior.groves);
  for (let g = 0; g < groves && budget > 0; g++) {
    const seed = farCell(ctx, start, 5, 4);
    if (seed < 0) continue;
    const size = Math.min(budget, rng.int(6, 14)); // bosquets plus massifs, moins d'obstacles isolés (quartiles Unity 1/1/4, max 35)
    const grove = [seed];
    const front = [seed];
    while (grove.length < size && front.length) {
      const cur = front[rng.int(0, front.length - 1)]!;
      const next = sideNeighbours(cur).filter((k) => free(k) && !grove.includes(k));
      if (!next.length) { front.splice(front.indexOf(cur), 1); continue; }
      const k = rng.pick(next);
      grove.push(k); front.push(k);
    }
    for (const i of grove) if (placeObject(ctx, rng.pick(b.interior.grove), i, 0.15)) budget--;
  }
  for (let attempt = 0; attempt < 400 && budget > 0; attempt++) {
    const i = farCell(ctx, start, 3.5, 3, 1);
    if (i < 0 || sideNeighbours(i).some((k) => ctx.blocked[k] && ctx.kind[k] === OPEN)) continue;
    if (placeObject(ctx, rng.pick(b.interior.single), i, 0.12)) budget--;
  }
  const f = b.feature;
  if (f.type === 'undergrowth') {
    let left = irange(rng, f.count);
    for (let attempt = 0; attempt < 400 && left > 0; attempt++) {
      const i = farCell(ctx, start, 3.5, 2, 1);
      if (i < 0 || !sideNeighbours(i).some((k) => ctx.blocked[k])) continue;
      if (placeObject(ctx, rng.pick(f.bushes), i, 0.15)) left--;
    }
  }
}

// --- 6. Point d'intérêt (R10) et décor (R6) ---------------------------------------------------------
function poi(ctx: Ctx, start: Vec2): number {
  const { b, rng } = ctx;
  for (let attempt = 0; attempt < 200; attempt++) {
    const i = rng.int(0, CELLS - 1);
    if (ctx.kind[i] !== OPEN || ctx.blocked[i]) continue;
    const d = Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y);
    if (d < 3.2 || d > 6.5 || cellsWithin(ctx, ctx.cx[i]!, ctx.cy[i]!, 1.6).some((k) => ctx.kind[k] === PATH)) continue;
    if (placeObject(ctx, rng.pick(b.poi), i, 0.02)) return i;
  }
  return -1;
}

function decor(ctx: Ctx): void {
  const { b, rng } = ctx;
  const meadow = b.feature.type === 'meadow' ? 1.4 : 1;
  let left = Math.round(irange(rng, b.decor.count) * meadow);
  for (let attempt = 0; attempt < 600 && left > 0; attempt++) {
    const c = rng.int(0, CELLS - 1);
    if (ctx.kind[c] !== OPEN || ctx.blocked[c]) continue;
    const n = rng.int(3, 6);
    for (const i of cellsWithin(ctx, ctx.cx[c]!, ctx.cy[c]!, 1.6)) {
      if (left <= 0 || ctx.kind[i] !== OPEN || ctx.blocked[i] || !rng.chance(n / 8)) continue;
      if (rng.chance(0.5)) placeObject(ctx, rng.pick(b.decor.objects), i, 0.35);
      else place(ctx, ctx.decals, rng.pick(b.decor.decals), i, 0.3);
      left--;
    }
  }
  for (let i = 0; i < CELLS; i++) if (ctx.kind[i] === PATH && rng.chance(b.path.slabChance)) place(ctx, ctx.decals, rng.pick(b.path.slabs), i, 0.2);
}

// --- 7. Sol -----------------------------------------------------------------------------------------
/** Tons de sol : 0 base, 1 clair, 2 sombre, 3 chemin, 4 eau, 5 parcelle, 6 anneau de clairière, 7 ponton (sans transition). */
const T_BASE = 0, T_LIGHT = 1, T_DARK = 2, T_PATH = 3, T_WATER = 4, T_SOIL = 5, T_RING = 6, T_PONTOON = 7;
/** Ordre de débordement (auto-tiling) : l'eau déborde sur tout, puis la terre des parcelles, le chemin, le clair... */
export const TONE_PRIORITY: readonly number[] = [T_WATER, T_SOIL, T_PATH, T_LIGHT, T_BASE, T_RING, T_DARK];

function toneLists(b: BiomeDef): Record<number, readonly string[]> {
  const f = b.feature;
  return { [T_BASE]: b.ground.base, [T_LIGHT]: b.ground.light, [T_DARK]: b.ground.dark, [T_PATH]: b.path.ground, [T_WATER]: f.type === 'lake' ? f.water : [], [T_SOIL]: f.type === 'plots' ? f.soil : [], [T_RING]: f.type === 'clearing' ? f.ring : [], [T_PONTOON]: f.type === 'lake' ? f.pontoon : [] };
}

/** Rang de débordement d'une tuile de sol (0 = la plus débordante), -1 si la tuile n'est pas un sol de la sous-zone. */
export function groundRank(b: BiomeDef, key: string): number {
  const lists = toneLists(b);
  return TONE_PRIORITY.findIndex((t) => lists[t]!.includes(key));
}

/** Paires [tuile débordante, tuile réceptrice] possibles dans une sous-zone (pièces à produire dans l'atlas). */
export function transitionPairs(b: BiomeDef): [string, string][] {
  const keys = [...new Set(TONE_PRIORITY.flatMap((t) => toneLists(b)[t]!))];
  const out: [string, string][] = [];
  for (const to of keys) for (const from of keys) if (groundRank(b, to) < groundRank(b, from)) out.push([to, from]);
  return out;
}

/**
 * Sol : une tuile de base par cellule (sans miroir : les motifs des tuiles Retro se raccordent), zones sombres par
 * bruit (et sous la bordure), taches claires ; puis auto-tiling : sur chaque cellule bordée par un côté d'un sol plus
 * débordant, des pièces de transition (bord adouci de la tuile voisine) cassent l'escalier de losanges.
 */
function ground(ctx: Ctx, start: Vec2): { tiles: { key: string; flip: boolean }[]; transitions: PlacedTile[] } {
  const { b, rng } = ctx;
  const noise = createNoise(rng, 7);
  const f = b.feature;
  const clearingR = f.type === 'clearing' ? Math.max(...cellsWithin(ctx, start.x, start.y, 9).filter((i) => ctx.protect[i]).map((i) => Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y)), 0) : 0;
  const tone = new Uint8Array(CELLS);
  for (let i = 0; i < CELLS; i++) {
    const k = ctx.kind[i];
    if (k === VOID) continue;
    const x = ctx.cx[i]!, y = ctx.cy[i]!, n = noise(x, y);
    tone[i] = k === PATH ? T_PATH : k === WATER ? T_WATER : k === PLOT ? T_SOIL : k === PONTOON ? T_PONTOON : k === BORDER ? T_DARK
      : f.type === 'clearing' && Math.hypot(x - start.x, y - start.y) > clearingR + 1 ? T_RING
        : n > b.ground.lightAbove ? T_LIGHT : n < b.ground.darkBelow ? T_DARK : T_BASE;
  }
  const lists = toneLists(b);
  const listOf = (t: number): readonly string[] => (lists[t]!.length ? lists[t]! : b.ground.base);
  const tiles: { key: string; flip: boolean }[] = [];
  const keys: string[] = new Array<string>(CELLS).fill('');
  for (let r = -1; r <= ROWS; r++) for (let c = -1; c <= COLS; c++) {
    const rr = Math.min(ROWS - 1, Math.max(0, r));
    const cc = Math.min(COLS - 1 - (rr & 1), Math.max(0, c));
    const i = idx(rr, cc), t = tone[i]!;
    const key = rng.pick(listOf(t));
    tiles.push({ key, flip: t !== T_WATER && t !== T_PONTOON && rng.chance(0.5) }); // l'eau en miroir forme des labyrinthes, les planches ont un relief
    if (r === rr && c === cc) keys[i] = key;
  }
  const rank = new Uint8Array(CELLS).fill(255);
  for (let i = 0; i < CELLS; i++) if (isValidIdx(i)) { const k = groundRank(b, keys[i]!); if (k >= 0) rank[i] = k; }
  const out: PlacedTile[] = [];
  for (const t of transitions(rank, keys, TONE_PRIORITY.map((_, k) => k))) {
    const c = centerOf(t.cell), variant = (Math.floor(t.cell / COLS) + t.cell) & 1;
    for (const piece of splitMask(t.mask)) out.push({ key: transitionKey(t.from, t.to, piece, variant), cell: t.cell, flip: false, x: c.x, y: c.y });
  }
  return { tiles, transitions: out };
}

// --- 8. Masque, validation (R11), apparitions ---------------------------------------------------------
function walkMask(ctx: Ctx): Bitset {
  const w = new Bitset(CELLS);
  for (let i = 0; i < CELLS; i++) if ((ctx.kind[i] === OPEN || ctx.kind[i] === PATH || ctx.kind[i] === PONTOON) && !ctx.blocked[i]) w.set(i);
  return w;
}

function carve(ctx: Ctx, from: Vec2, to: Vec2): void {
  const n = Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) * 4);
  const cleared = new Set<number>();
  for (let k = 0; k <= n; k++) {
    const x = from.x + ((to.x - from.x) * k) / n, y = from.y + ((to.y - from.y) * k) / n;
    for (const i of cellsWithin(ctx, x, y, 0.9)) {
      if (ctx.kind[i] === VOID) continue;
      ctx.kind[i] = PATH; ctx.blocked[i] = 0; cleared.add(i);
    }
  }
  ctx.objects = ctx.objects.filter((o) => {
    if (ctx.content.tiles[o.key]?.block !== 1) return true;
    const cells = footprintCells(ctx.content.tiles, o.key, o.cell);
    if (!cells.some((c) => cleared.has(c))) return true;
    for (const c of cells) ctx.blocked[c] = 0;
    return false;
  });
}

function pickSpawns(ctx: Ctx, walk: Bitset, startCell: number, start: Vec2): Vec2[] {
  const d = bfs(walk, startCell);
  const cand: number[] = [];
  for (let i = 0; i < CELLS; i++) if (d[i]! > 0 && Math.hypot(ctx.cx[i]! - start.x, ctx.cy[i]! - start.y) > 7) cand.push(i);
  const out: Vec2[] = [];
  const order = shuffled(ctx.rng, cand.length);
  for (const k of order) {
    const i = cand[k]!;
    if (out.some((p) => Math.hypot(p.x / CELL_W - ctx.cx[i]!, p.y / CELL_H - ctx.cy[i]!) < 4)) continue;
    out.push(centerOf(i));
    if (out.length >= 12) break;
  }
  return out;
}

export interface GenerateOptions { boss?: boolean; name?: string; id?: string; }

/** Génère une salle de la sous-zone `biomeId` (déterministe pour un même `seed`). */
export function generateRoom(content: Content, biomeId: string, seed: number, opts: GenerateOptions = {}): RoomDef {
  const b = content.biomes[biomeId];
  if (!b) throw new Error(`Sous-zone inconnue : ${biomeId}`);
  const rng = createRng(seed);
  const ctx: Ctx = {
    rng, b, content, kind: new Uint8Array(CELLS), protect: new Uint8Array(CELLS), blocked: new Uint8Array(CELLS),
    objects: [], decals: [], cx: new Float32Array(CELLS), cy: new Float32Array(CELLS),
  };
  for (let i = 0; i < CELLS; i++) {
    if (!isValidIdx(i)) { ctx.kind[i] = VOID; continue; }
    const c = centerOf(i);
    ctx.cx[i] = c.x / CELL_W; ctx.cy[i] = c.y / CELL_H;
  }
  // gabarit
  const exits = chooseExits(rng);
  const s0 = centerOf(cellAt((SPAN_X / 2 + rng.int(-2, 2) * 0.5) * CELL_W, (SPAN_Y / 2 + rng.int(-2, 2) * 0.5) * CELL_H));
  const start = { x: s0.x / CELL_W, y: s0.y / CELL_H };
  for (const i of cellsWithin(ctx, start.x, start.y, 2.6)) ctx.protect[i] = 1;
  for (const e of exits) paintPath(ctx, paintCorridor(ctx, e), start, range(rng, b.path.width));
  if (exits.length === 1 && rng.chance(0.6)) {
    // branche sans issue vers un autre côté : la salle ne se réduit pas à un couloir
    const a = rng.next() * Math.PI * 2;
    paintPath(ctx, start, { x: start.x + Math.cos(a) * 8, y: start.y + Math.sin(a) * 6 }, range(rng, b.path.width) * 0.85);
  }
  feature(ctx, start);
  border(ctx);
  interior(ctx, start);
  const poiCell = poi(ctx, start);
  decor(ctx);
  // validation : chaque sortie doit être atteignable depuis le départ
  const startCell = cellAt(s0.x, s0.y);
  const exitCells = exits.map((e) => {
    const x = Math.min(SPAN_X - 0.3, Math.max(0.3, e.ex)), y = Math.min(SPAN_Y - 0.3, Math.max(0.3, e.ey));
    return cellsWithin(ctx, x, y, 1.2).sort((p, q) => Math.hypot(ctx.cx[p]! - x, ctx.cy[p]! - y) - Math.hypot(ctx.cx[q]! - x, ctx.cy[q]! - y))[0]!;
  });
  let walk = walkMask(ctx);
  for (let k = 0; k < exits.length; k++) {
    const d = bfs(walk, startCell);
    if (d[exitCells[k]!]! > 0) continue;
    carve(ctx, { x: ctx.cx[exitCells[k]!]!, y: ctx.cy[exitCells[k]!]! }, start);
    walk = walkMask(ctx);
  }
  const groundTiles = ground(ctx, start);
  const decals = [...groundTiles.transitions, ...ctx.decals];
  const spawns = pickSpawns(ctx, walk, startCell, start);
  return {
    id: opts.id ?? `${biomeId}-${seed >>> 0}`,
    name: opts.name ?? b.name,
    map: `tiles:${biomeId}`,
    width: ROOM_W, height: ROOM_H,
    walkable: [{ x: 0, y: 0, w: ROOM_W, h: ROOM_H }],
    playerStart: s0,
    spawns,
    waves: [],
    grid: {
      walk: walk.words, biome: biomeId, ground: groundTiles.tiles, decals, objects: ctx.objects,
      exits: exits.map((e, k) => ({ cell: exitCells[k]!, side: e.side, x: Math.min(ROOM_W, e.ex * CELL_W), y: Math.min(ROOM_H, e.ey * CELL_H) })),
      poi: poiCell,
      ...(b.ground.tint ? { tint: b.ground.tint } : {}),
    },
  };
}
